import { schemas, CALL_API, SERVICES } from "state/middleware/api";
import { reduxStoreExpiry, noop } from "lib";
import * as types from "./teamTemplateSizesTypes";
import {
  isFetchingTeamTemplateSizesSelector,
  teamTemplateSizesLastFetchedSelector,
  teamTemplateSizesCachedTeamIdSelector
} from "./teamTemplateSizesSelectors";

export type MeasurementUnits = "px" | "mm" | "in";
export type TemplateType = "image" | "print";

export interface TeamTemplateSize {
  id: string;
  teamId: string;
  name: string;
  width: number;
  height: number;
  measurementUnits: MeasurementUnits;
  templateType: TemplateType;
  active: boolean;
  doubleSided: boolean;
  widthInMm?: number;
  heightInMm?: number;
  widthInInches?: number;
  heightInInches?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface TeamTemplateSizeInput {
  name: string;
  width: number;
  height: number;
  measurementUnits: MeasurementUnits;
  templateType: TemplateType;
  active: boolean;
  doubleSided: boolean;
}

const PAGE_SIZE = 100;
const TTL_MINUTES = 5;

type Dispatch = (action: any) => any;
type GetState = () => any;

export const fetchTeamTemplateSizes =
  ({
    teamId,
    page = 1,
    onSuccess = noop,
    onFailure = noop
  }: {
    teamId: string;
    page?: number;
    onSuccess?: (response: any) => void;
    onFailure?: (response: any) => void;
  }) =>
  (dispatch: Dispatch, getState: GetState) => {
    return dispatch({
      [CALL_API]: {
        method: "GET",
        service: SERVICES.ASSET,
        endpoint: `/teams/${teamId}/template-sizes`,
        request: {
          page,
          pageSize: PAGE_SIZE
        },
        types: [
          types.FETCH_TEAM_TEMPLATE_SIZES_REQUEST,
          types.FETCH_TEAM_TEMPLATE_SIZES_SUCCESS,
          types.FETCH_TEAM_TEMPLATE_SIZES_FAILURE
        ],
        schema: schemas.TEAM_TEMPLATE_SIZES,
        extra: { teamId, pageSize: PAGE_SIZE },
        onSuccess: (response: any) => {
          if (response && response.ids && response.ids.length === PAGE_SIZE) {
            const cachedTeamId =
              teamTemplateSizesCachedTeamIdSelector(getState());
            if (cachedTeamId !== teamId) {
              // Team switched mid-pagination; stop recursing so we don't
              // stomp the new team's in-flight fetch.
              return;
            }
            dispatch(
              fetchTeamTemplateSizes({
                teamId,
                page: page + 1,
                onSuccess,
                onFailure
              })
            );
          } else {
            onSuccess(response);
          }
        },
        onFailure
      }
    });
  };

export const fetchTeamTemplateSizesIfNeeded =
  ({
    teamId,
    ttlMinutes = TTL_MINUTES,
    onSuccess = noop,
    onFailure = noop
  }: {
    teamId: string;
    ttlMinutes?: number;
    onSuccess?: (response: any) => void;
    onFailure?: (response: any) => void;
  }) =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const cachedTeamId = teamTemplateSizesCachedTeamIdSelector(state);
    const lastFetched = teamTemplateSizesLastFetchedSelector(state);
    const isSameTeam = cachedTeamId === teamId;

    if (isSameTeam && isFetchingTeamTemplateSizesSelector(state)) return;
    if (isSameTeam && !reduxStoreExpiry.isDataExpired(lastFetched, ttlMinutes))
      return;

    dispatch(fetchTeamTemplateSizes({ teamId, onSuccess, onFailure }));
  };

export const addTeamTemplateSize =
  ({
    teamId,
    size,
    onSuccess = noop,
    onFailure = noop
  }: {
    teamId: string;
    size: TeamTemplateSizeInput;
    onSuccess?: (response: any) => void;
    onFailure?: (response: any) => void;
  }) =>
  (dispatch: Dispatch) => {
    return dispatch({
      [CALL_API]: {
        method: "POST",
        service: SERVICES.ASSET,
        endpoint: `/teams/${teamId}/template-sizes`,
        request: {
          body: size
        },
        types: [
          types.CREATE_TEAM_TEMPLATE_SIZE_REQUEST,
          types.CREATE_TEAM_TEMPLATE_SIZE_SUCCESS,
          types.CREATE_TEAM_TEMPLATE_SIZE_FAILURE
        ],
        schema: schemas.TEAM_TEMPLATE_SIZE,
        extra: { teamId },
        onSuccess,
        onFailure
      }
    });
  };

export const updateTeamTemplateSize =
  ({
    teamId,
    sizeId,
    size,
    onSuccess = noop,
    onFailure = noop
  }: {
    teamId: string;
    sizeId: string;
    size: TeamTemplateSizeInput;
    onSuccess?: (response: any) => void;
    onFailure?: (response: any) => void;
  }) =>
  (dispatch: Dispatch) => {
    return dispatch({
      [CALL_API]: {
        method: "PUT",
        service: SERVICES.ASSET,
        endpoint: `/teams/${teamId}/template-sizes/${sizeId}`,
        request: {
          body: size
        },
        types: [
          types.UPDATE_TEAM_TEMPLATE_SIZE_REQUEST,
          types.UPDATE_TEAM_TEMPLATE_SIZE_SUCCESS,
          types.UPDATE_TEAM_TEMPLATE_SIZE_FAILURE
        ],
        schema: schemas.TEAM_TEMPLATE_SIZE,
        extra: { teamId },
        onSuccess,
        onFailure
      }
    });
  };

export const deleteTeamTemplateSize =
  ({
    teamId,
    sizeId,
    onSuccess = noop,
    onFailure = noop
  }: {
    teamId: string;
    sizeId: string;
    onSuccess?: (response: any) => void;
    onFailure?: (response: any) => void;
  }) =>
  (dispatch: Dispatch) => {
    return dispatch({
      [CALL_API]: {
        method: "DELETE",
        service: SERVICES.ASSET,
        endpoint: `/teams/${teamId}/template-sizes/${sizeId}`,
        types: [
          types.DELETE_TEAM_TEMPLATE_SIZE_REQUEST,
          types.DELETE_TEAM_TEMPLATE_SIZE_SUCCESS,
          types.DELETE_TEAM_TEMPLATE_SIZE_FAILURE
        ],
        schema: schemas.NONE,
        extra: { sizeId, teamId },
        onSuccess,
        onFailure
      }
    });
  };
